// ReceiptViewModal — v1.9.9
// Single source of truth for how a Cash Receipt looks. Uses the same
// `buildReceiptBlock` HTML the print / PDF pipelines emit, dropped in via
// dangerouslySetInnerHTML so what's on screen exactly matches what the
// downloaded PDF and printed page show. No parallel DOM to drift.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiDownload, FiPrinter, FiX, FiShare2 } from 'react-icons/fi';
import { buildReceiptBlock, RECEIPT_STYLES, printCashReceipt, downloadCashReceipt, shareCashReceipt, prepareCashReceiptShare } from '../utils/printReceipt';

import { useIsMobile } from '../utils/platform';

const ReceiptViewModal = ({ cr, profile = {}, onClose }) => {
  const isMobile = useIsMobile();
  const sheetRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const html = useMemo(() => (cr ? buildReceiptBlock(cr, profile, 'ORIGINAL') : ''), [cr, profile]);
  // Build the share PDF while the user reads the receipt, so Share is instant.
  useEffect(() => { if (cr) prepareCashReceiptShare(cr, profile); }, [cr, profile]);
  if (!cr) return null;

  const share = async () => {
    try {
      const res = await shareCashReceipt(cr, profile);
      // eslint-disable-next-line no-alert
      if (res?.shared === 'needs-tap') alert('Receipt ready — tap Share again');
    } catch (e) { /* eslint-disable-next-line no-alert */ alert(e?.message || 'Could not share the receipt'); }
  };

  // v1.9.16 — route through the same downloadCashReceipt path the print
  // pipeline uses. That renders the receipt into a fresh 190mm-wide
  // off-screen container sized for A4, avoiding the modal's own 780px
  // maxWidth which produced a shrunken PDF with excess whitespace.
  const download = async () => {
    setDownloading(true);
    try { await downloadCashReceipt(cr, profile); }
    catch (_) { /* eslint-disable-next-line no-alert */ alert('Could not generate PDF. Try again or take a screenshot.'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ alignItems: 'flex-start', justifyContent: 'center', padding: 12, overflowY: 'auto' }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 900, marginTop: 20, marginBottom: 20, background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', flexWrap: 'wrap', gap: 6 }}>
          <strong style={{ fontSize: 14, color: '#111827' }}>Receipt {cr.cr_number}</strong>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!isMobile && (
              <button className="btn btn-sm" onClick={() => printCashReceipt(cr, profile)} title="Print"><FiPrinter /> Print</button>
            )}
            <button className="btn btn-sm" onClick={share} title="Share (WhatsApp, email, …)"><FiShare2 /> Share</button>
            <button className="btn btn-sm btn-primary" onClick={download} disabled={downloading}>
              <FiDownload /> {downloading ? '...' : 'Download PDF'}
            </button>
            <button className="btn-icon" onClick={onClose}><FiX /></button>
          </div>
        </div>

        {/* v1.9.9 — the exact same HTML the print / PDF pipeline emits.
            No parallel modal DOM to drift out of sync with the printed CR. */}
        <div style={{ background: '#eef1e8', padding: 12 }}>
          <style dangerouslySetInnerHTML={{ __html: RECEIPT_STYLES }} />
          <div
            ref={sheetRef}
            style={{ background: '#fff', maxWidth: 780, margin: '0 auto' }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </div>
    </div>
  );
};

export default ReceiptViewModal;
